import { Icon, IconID } from '@hub/common/Icon/Icon';
import { useState, useEffect } from 'react';

export enum ProductTableSortingState {
  NoSort = 'no_sort',
  Ascending = 'ascending',
  Descending = 'descending',
}

export type ProductTableHeaders = {
  key: string;
  value: string;
  sortingState: ProductTableSortingState;
  sortable: boolean;
  styles?: string;
  infoIcon?: boolean | undefined;
  subHeaders?: ProductTableHeaders[];
};

export type ProductTableHeadersProps = {
  headers: ProductTableHeaders[];
  sort: (key: string, sortingState: ProductTableSortingState) => void;
  openInfoModal?: (key: string) => void;
};

export const ProductTableHeader = ({ headers, sort, openInfoModal }: ProductTableHeadersProps) => {
  const [tableHeaders, setTableHeaders] = useState(headers);

  useEffect(() => {
    setTableHeaders(headers);
  }, [headers]);

  const sortingButton = (header: ProductTableHeaders) => (
    <div
      data-testid={`product-table-sort-button-${header.key}`}
      className={`ml-2 flex  ${
        header.sortingState === ProductTableSortingState.NoSort || !header.sortable ? 'invisible' : 'visible'
      } ${header.sortingState === ProductTableSortingState.Descending ? 'rotate-180' : 'rotate-0'}`}
    >
      <Icon size={5} id={IconID.ARROW_TABLE_SORTING} />
    </div>
  );
  const infoButton = (header: ProductTableHeaders) => (
    <div
      className="ml-2"
      data-testid={`product-table-info-button-${header.key}`}
      role="button"
      tabIndex={0}
      aria-label={`${header.value}`}
      onClick={event => {
        event.stopPropagation();
        if (openInfoModal) {
          openInfoModal(header.value);
        }
      }}
    >
      <Icon size={5} id={IconID.INFO_CIRCLE_GRAY} />
    </div>
  );
  const cellHeader = (header: ProductTableHeaders) => (
    <div
      className={` ${
        header.sortingState === ProductTableSortingState.NoSort
          ? 'text-white font-normal'
          : 'text-igus-primary font-medium'
      } ${header.styles || ''} `}
      data-testid={`cell-header-${header.key}`}
      role="button"
      tabIndex={0}
      onClick={() => sort(header.key, header.sortingState)}
      key={header.key}
    >
      <div className="flex items-center  select-none">
        {header.value}
        {header.infoIcon && infoButton(header)}
        {sortingButton(header)}
      </div>
    </div>
  );
  const cellHeaderWithSubheader = (header: ProductTableHeaders) => (
    <div className="h-[68px] flex flex-col justify-between mr-8">
      <div className="text-white font-normal bg-[#333333] w-full h-1/2 text-center flex justify-center items-center">
        {header.value}
      </div>
      <div className="flex justify-between h-1/2 items-center">
        {header.subHeaders.map(subHeader => cellHeader(subHeader))}
      </div>
    </div>
  );

  return (
    <thead className={`bg-black text-left text-sm `} data-testid="product-table-header">
      <tr className="border border-black border-t-0 h-[68px]" data-testid="product-table-header-container">
        {tableHeaders.map(header => (
          <th key={header.key}>{header.subHeaders ? cellHeaderWithSubheader(header) : cellHeader(header)}</th>
        ))}
      </tr>
    </thead>
  );
};
